import React, {useCallback, useEffect, useState} from 'react';
import {View, StyleSheet, FlatList, Image} from 'react-native';
import {
  Text,
  ActivityIndicator,
  IconButton,
  Snackbar,
  FAB,
  Chip,
  TouchableRipple,
} from 'react-native-paper';
import GradientHeader from '../../components/common/GradientHeader';
import {useTranslation} from 'react-i18next';
import {useNavigation, DrawerActions} from '@react-navigation/native';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {colors, spacing} from '../../theme';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

const ORG_TYPES = [
  {value: 'OUTLET', label: 'Outlet'},
  {value: 'FRANCHISE', label: 'Franchise'},
  {value: 'MASTER_FRANCHISE', label: 'Master Franchise'},
  {value: 'WAREHOUSE', label: 'Warehouse'},
  {value: 'OWNER_GROUP', label: 'Owner Group'},
];

const StoreListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {orgUnits, isLoading, fetchOrgUnits} =
    useOrgUnitStore();

  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    fetchOrgUnits();
  }, [fetchOrgUnits]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      fetchOrgUnits();
    });
    return unsubscribe;
  }, [navigation, fetchOrgUnits]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchOrgUnits();
    setRefreshing(false);
  }, [fetchOrgUnits]);

  const getOrgTypeLabel = useCallback(value => {
    const found = ORG_TYPES.find(ot => ot.value === value);
    return found ? found.label : value;
  }, []);

  const formatExpiryDate = useCallback(dateStr => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }, []);

  const isExpired = useCallback(item => {
    if (item.subscriptionStatus === 'EXPIRED' ||
        item.subscriptionStatus === 'SUSPENDED') return true;
    if (item.planExpiryDate) {
      const expiry = new Date(item.planExpiryDate);
      return expiry < new Date();
    }
    return false;
  }, []);

  const getPlanStatusColor = useCallback(item => {
    if (!item.subscriptionStatus) return colors.textSecondary;
    switch (item.subscriptionStatus) {
      case 'ACTIVE':
        return colors.success;
      case 'TRIAL':
        return colors.info;
      case 'EXPIRED':
      case 'SUSPENDED':
        return colors.error;
      default:
        return colors.textSecondary;
    }
  }, []);

  const getPlanLabel = useCallback((item, tFn) => {
    if (!item.planName) return tFn('store.noPlan');
    if (item.subscriptionStatus === 'TRIAL') {
      return item.planName + ' (' + tFn('store.trial') + ')';
    }
    return item.planName;
  }, []);

  const renderItem = useCallback(
    ({item}) => {
      const planColor = getPlanStatusColor(item);
      const expired = isExpired(item);
      const locationParts = [item.area, item.city].filter(Boolean);
      const locationText = locationParts.join(', ');

      return (
        <TouchableRipple
          onPress={() =>
            navigation.navigate('StoreView', {storeId: item.id})
          }
          rippleColor={colors.primary + '20'}>
        <View style={styles.row}>
          {/* Store Image - ~20% */}
          <View style={styles.imageContainer}>
            {item.photoUrl || item.logoUrl ? (
              <Image
                source={{uri: item.photoUrl || item.logoUrl}}
                style={styles.storeImage}
                resizeMode="cover"
              />
            ) : (
              <View style={styles.imagePlaceholder}>
                <Text style={styles.imagePlaceholderText}>
                  {item.name?.charAt(0)?.toUpperCase() || 'S'}
                </Text>
              </View>
            )}
          </View>

          {/* Info Block */}
          <View style={styles.infoBlock}>
            {/* Row 1: Name */}
            <Text style={styles.storeName} numberOfLines={1}>
              {item.name}
            </Text>

            {/* Row 2: Plan & Expiry */}
            <View style={styles.planRow}>
              <Text style={[styles.planText, {color: planColor}]}>
                {getPlanLabel(item, t)}
              </Text>
              {item.planExpiryDate ? (
                <Text
                  style={[
                    styles.expiryText,
                    expired && styles.expiredText,
                  ]}>
                  {expired
                    ? t('store.expired') + ': ' + formatExpiryDate(item.planExpiryDate)
                    : t('store.expires') + ': ' + formatExpiryDate(item.planExpiryDate)}
                </Text>
              ) : null}
            </View>

            {/* Row 3: Worker & Order counts */}
            <View style={styles.statsRow}>
              <View style={styles.statBadge}>
                <Text style={styles.statBadgeText}>{item.workerCount ?? 0} Workers</Text>
              </View>
              <View style={styles.statBadge}>
                <Text style={styles.statBadgeText}>{item.activeOrderCount ?? 0} Orders</Text>
              </View>
            </View>

            {/* Row 4: Location */}
            {locationText ? (
              <View style={styles.locationRow}>
                <Icon name="map-marker-outline" size={14} color={colors.textSecondary} />
                <Text style={styles.locationText} numberOfLines={1}>
                  {locationText}
                </Text>
              </View>
            ) : null}

            {/* Row 5: Org Type & Actions */}
            <View style={styles.metaRow}>
              <Chip
                style={styles.typeChip}
                textStyle={styles.typeChipText}
                compact>
                <Text style={styles.typeChipLabel}>
                  {getOrgTypeLabel(item.orgType)}
                </Text>
              </Chip>
              <View style={styles.actionsRow}>
                <IconButton
                  icon="pencil-outline"
                  size={18}
                  iconColor={colors.primary}
                  onPress={() =>
                    navigation.navigate('StoreForm', {storeId: item.id})
                  }
                  style={styles.actionBtn}
                />
                <TouchableRipple
                  onPress={() => {
                    navigation.navigate('PlanSelection', {
                      orgUnitId: item.id,
                      orgUnitName: item.name,
                      subscriptionId: item.subscriptionId,
                    });
                  }}
                  style={[styles.planActionBtn, expired && styles.planActionBtnExpired]}>
                  <Text style={[styles.planActionText, expired && styles.planActionTextExpired]}>
                    {expired ? t('store.renew') : t('store.upgrade')}
                  </Text>
                </TouchableRipple>
              </View>
            </View>
          </View>
        </View>
        </TouchableRipple>
      );
    },
    [
      navigation,
      getOrgTypeLabel,
      formatExpiryDate,
      isExpired,
      getPlanStatusColor,
      getPlanLabel,
      t,
    ],
  );

  const keyExtractor = useCallback(item => String(item.id), []);

  const ListHeader = useCallback(
    () => (
      <View style={styles.descriptionRow}>
        <Text style={styles.descriptionText}>
          {t('store.description')}
        </Text>
      </View>
    ),
    [t],
  );

  const ListEmpty = useCallback(
    () => (
      <View style={{alignItems: 'center', paddingTop: 60}}>
        <Icon name="store-outline" size={56} color={colors.textLight} />
        <Text style={styles.emptyText}>{t('common.noData')}</Text>
      </View>
    ),
    [t],
  );

  if (isLoading && orgUnits.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader
        title={t('store.title')}
        onMenu={() => navigation.dispatch(DrawerActions.openDrawer())}
      />

      <FlatList
        data={orgUnits}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        ListHeaderComponent={ListHeader}
        ListEmptyComponent={ListEmpty}
        contentContainerStyle={styles.listContent}
        refreshing={refreshing}
        onRefresh={handleRefresh}
      />

      <FAB
        icon="plus"
        style={styles.fab}
        onPress={() => navigation.navigate('StoreForm')}
        color={colors.white}
      />

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  descriptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: colors.surface,
  },
  descriptionText: {
    flex: 1,
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  fab: {
    position: 'absolute',
    right: spacing.md,
    bottom: spacing.md,
    backgroundColor: colors.primary,
  },
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
  },

  listContent: {padding: spacing.sm, paddingBottom: 80, gap: spacing.sm},
  // Row layout
  row: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    padding: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },

  // Image (~20% of row width)
  imageContainer: {
    width: 72,
    height: 72,
    borderRadius: 10,
    overflow: 'hidden',
    marginRight: spacing.sm + 4,
    backgroundColor: colors.background,
  },
  storeImage: {
    width: '100%',
    height: '100%',
  },
  imagePlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.primary + '15',
  },
  imagePlaceholderText: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.primary,
  },

  // Info block
  infoBlock: {
    flex: 1,
    justifyContent: 'center',
  },
  storeName: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 3,
  },

  // Plan row
  planRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 5,
  },
  planText: {
    fontSize: 13,
    fontWeight: '600',
  },
  expiryText: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  expiredText: {
    color: colors.error,
    fontWeight: '600',
  },
  // Stats row
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 5,
  },
  statBadge: {
    backgroundColor: colors.primary + '10',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  statBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.primary,
  },

  // Meta row (org type + actions)
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  typeChip: {
    backgroundColor: colors.textSecondary + '18',
    height: 24,
  },
  typeChipText: {
    fontSize: 11,
    color: colors.textSecondary,
  },
  typeChipLabel: {
    fontSize: 11,
    color: colors.textSecondary,
    marginTop: 0,
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5,
  },
  locationText: {
    fontSize: 12,
    color: colors.textSecondary,
    flex: 1,
  },

  // Actions row
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionBtn: {
    margin: 0,
    width: 32,
    height: 32,
  },
  planActionBtn: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: colors.primary + '12',
  },
  planActionBtnExpired: {
    backgroundColor: colors.error,
  },
  planActionText: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.primary,
  },
  planActionTextExpired: {
    color: colors.white,
  },
});

export default StoreListScreen;
